import { Controller } from '@hotwired/stimulus'
import { getComponent, Component } from '@symfony/ux-live-component'
import { mercureSubscribe, mercureUnsubscribe } from '@/service/mercure'
import ModalController from '@/public/components/Modal/controller'
import LotCompletionTimeController from '@/public/blocks/LotCompletionTime/controller'
import LotCardController from '@/public/blocks/LotCard/controller'

type LiveComponent = {
    on(event: string, callback: () => void): void
}

/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static outlets = ['lot-completion-time', 'modal', 'lot-card']
    static targets = [
        'loader',
        'lotPrice',
        'lotsContainer',
        'mobileBrandInput',
        'mobileModelInput',
        'mobileModelMenu',
        'topBrandInput',
        'topModelInput',
        'slider',
        'sliderPrev',
        'sliderNext'
    ]
    static values = { mercureUrl: String, userHash: String }

    private liveComponent: LiveComponent | null = null
    private observer: IntersectionObserver | null = null
    private brands: Record<string, { name: string; models: Record<string, string> }> = {}
    private swiper: any | null = null

    declare lotCompletionTimeOutlets: LotCompletionTimeController[]
    declare hasLotCompletionTimeOutlet: boolean

    declare modalOutlet: ModalController

    declare lotCardOutlets: LotCardController[]
    declare hasLotCardOutlet: boolean

    declare mercureUrlValue: string
    declare userHashValue: string
    declare hasUserHashValue: boolean
    declare loaderTarget: HTMLElement
    declare lotsContainerTarget: HTMLElement
    declare lotPriceTargets: HTMLElement[]
    declare sliderTarget: HTMLElement
    declare sliderPrevTarget: HTMLElement
    declare sliderNextTarget: HTMLElement

    declare mobileBrandInputTarget: HTMLInputElement
    declare hasMobileBrandInputTarget: boolean
    declare mobileModelInputTarget: HTMLInputElement
    declare hasMobileModelInputTarget: boolean
    declare mobileModelMenuTarget: HTMLElement
    declare hasMobileModelMenuTarget: boolean
    declare topBrandInputTarget: HTMLInputElement
    declare hastopBrandInputTarget: boolean
    declare topModelInputTarget: HTMLInputElement

    connect() {
        window.addEventListener('bid:placed', this.handleBidPlaced as EventListener)
        window.addEventListener('bid:outbid', this.handleBidOutbid as EventListener)

        if (this.hastopBrandInputTarget || this.hasMobileBrandInputTarget) {
            requestAnimationFrame(() => requestAnimationFrame(() => this.loadBrands()))
        }
    }

    disconnect() {
        window.removeEventListener('bid:placed', this.handleBidPlaced as EventListener)
        window.removeEventListener('bid:outbid', this.handleBidOutbid as EventListener)
    }

    async initialize() {
        try {
            this.liveComponent = await getComponent(this.element) as LiveComponent;
            this.liveComponent?.on('render:started', this.handleRenderStarted);
        } catch {}
    }

    async sliderTargetConnected(element: HTMLElement) {
        const [{ default: Swiper }, { Navigation }] = await Promise.all([
            import('swiper'),
            import('swiper/modules')
        ])

        // @ts-ignore
        await import('swiper/swiper-bundle.css')

        this.swiper = new Swiper(element, {
            modules: [Navigation],
            spaceBetween: 24,
            grabCursor: true,
            slidesPerView: 1,
            navigation: {
                nextEl: this.sliderNextTarget,
                prevEl: this.sliderPrevTarget
            },
            breakpoints: {
                1280: {
                    slidesPerView: 2,
                }
            }
        })
    }

    sliderTargetDisconnected(element: HTMLElement) {
        this.swiper?.destroy()
    }

    mobileBrandInputTargetConnected(element: HTMLElement) {
        element.addEventListener('change', this.handleMobileBrandInputChange)
    }

    mobileBrandInputTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('change', this.handleMobileBrandInputChange)
    }

    mobileModelInputTargetConnected(element: HTMLElement) {
        element.addEventListener('change', this.handleMobileModelInputChange)
    }

    mobileModelInputTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('change', this.handleMobileModelInputChange)
    }

    topBrandInputTargetConnected(element: HTMLElement) {
        element.addEventListener('focus', this.handleTopBrandInputFocus)
    }

    topBrandInputTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('focus', this.handleTopBrandInputFocus)
    }

    topModelInputTargetConnected(element: HTMLElement) {
        element.addEventListener('focus', this.handleTopModelInputFocus)
    }

    topModelInputTargetDisconnected(element: HTMLElement) {
        element.removeEventListener('focus', this.handleTopModelInputFocus)
    }

    loaderTargetConnected(element: HTMLElement) {
        this.observer ??= new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.dispatchEvent(new CustomEvent('lot-list:more', { detail: {entry} }))
                }
            })
        })

        this.observer?.observe(element)
    }

    loaderTargetDisconnected(element: HTMLElement) {
        this.observer?.unobserve(element)
    }

    lotsContainerTargetConnected(element: HTMLElement) {
        if (!this.mercureUrlValue) {
            return
        }

        requestAnimationFrame(() => requestAnimationFrame(() => mercureSubscribe(this.mercureUrlValue, this.handleMercureMessage)))
    }

    lotsContainerTargetDisconnected(element: HTMLElement) {
        if (!this.mercureUrlValue) {
            return
        }

        mercureUnsubscribe(this.mercureUrlValue, this.handleMercureMessage)
    }

    private handleMercureMessage = (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data)
            const eventType = data?.event
            const payload = data?.payload

            if (data?.type === 'auction' && eventType === 'price_changed') {
                return this.handlePriceChanged(payload)
            }

            if (data?.type === 'auction' && eventType === 'expiration_changed') {
                return this.handleExpirationChanged(payload)
            }

            if (data?.type === 'bid_status' && eventType === 'bid_status_changed') {
                return this.handleBidStatusChanged(payload)
            }
        } catch {}
    }

    private handlePriceChanged(payload: { lotNumber?: number; html?: string; price?: number; leaderUserHash?: string }) {
        const lotNumber = payload?.lotNumber
        const html = payload?.html
        const price = payload?.price
        const leaderUserHash = payload?.leaderUserHash

        if (!lotNumber) {
            return
        }

        if (this.hasLotCardOutlet && price !== undefined) {
            const isLeading = this.hasUserHashValue && this.userHashValue === leaderUserHash
            const userLastBid = isLeading ? price : undefined

            this.lotCardOutlets?.forEach((lotCard) => {
                if (lotCard.lotNumberValue === lotNumber) {
                    lotCard.updateBidStatus(isLeading, price, userLastBid)
                }
            })
        }

        if (!html) {
            return
        }

        this.lotPriceTargets?.forEach((priceNode: HTMLElement) => {
            if (priceNode.dataset.lotNumber === String(lotNumber)) {
                priceNode.innerHTML = html
            }
        })
    }

    private handleExpirationChanged(payload: { lotNumber?: number; expirationTime?: number }) {
        const lotNumber = payload?.lotNumber
        const timestamp = payload?.expirationTime

        if (!lotNumber || !timestamp) {
            return
        }

        if (!this.hasLotCompletionTimeOutlet) {
            return
        }

        this.lotCompletionTimeOutlets?.forEach((lotCompletionTime) => {
            if (String(lotNumber) === String(lotCompletionTime.lotNumberValue)) {
                lotCompletionTime.updateTimestamp(timestamp)
            }
        })
    }

    private handleBidStatusChanged(payload: { lotNumber: number; isLeading: boolean; currentPrice: number; userLastBid?: number }) {
        const lotNumber = payload?.lotNumber

        if (!lotNumber || !this.hasLotCardOutlet) {
            return
        }

        this.lotCardOutlets?.forEach((lotCard) => {
            if (lotCard.lotNumberValue !== lotNumber) {
                return
            }

            lotCard.updateBidStatus(
                payload.isLeading ?? false,
                payload.currentPrice,
                payload.userLastBid
            )
        })
    }

    private handleBidPlaced = (event: CustomEvent) => {
        if (!this.hasLotCardOutlet) {
            return
        }

        const detail = event.detail as { lotNumber?: number; price?: number }
        const lotNumber = detail?.lotNumber
        const price = detail?.price

        if (!lotNumber || !price) {
            return
        }

        this.lotCardOutlets?.forEach((lotCard) => {
            if (lotCard.lotNumberValue !== lotNumber) {
                return
            }

            lotCard.updateBidStatus(true, price, price)
        })
    }

    private handleBidOutbid = (event: CustomEvent) => {
        if (!this.hasLotCardOutlet) {
            return
        }

        const detail = event.detail as { lotNumber?: number; price?: number; leaderUserHash?: string }
        const lotNumber = detail?.lotNumber
        const price = detail?.price
        const leaderUserHash = detail?.leaderUserHash

        if (!lotNumber || price == null) {
            return
        }

        const isLeading = this.hasUserHashValue && this.userHashValue === leaderUserHash
        const userLastBid = isLeading ? price : undefined

        this.lotCardOutlets?.forEach((lotCard) => {
            if (lotCard.lotNumberValue !== lotNumber) {
                return
            }

            lotCard.updateBidStatus(isLeading, price, userLastBid)
        })
    }

    private handleMobileBrandInputChange = async (event: Event) => {
        const target = event.target as HTMLInputElement
        const brandId = target.value ? parseInt(target.value) : null

        if (!brandId) {
            return this.resetModelOptions()
        }

        this.updateMobileModels(brandId)
    }

    private handleMobileModelInputChange = async (event: Event) => {
        const target = event.target as HTMLInputElement
        const modelId = target.value ? parseInt(target.value) : null

        if (!modelId || !this.hasMobileBrandInputTarget) {
            return
        }

        const brandId = this.findBrandByModelId(modelId)

        if (brandId) {
            this.updateMobileBrand(brandId, this.brands[brandId]?.name || '')
        }
    }

    private handleRenderStarted = () => {
        this.clearModelOptions()
    }

    private handleTopBrandInputFocus = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        this.modalOutlet?.open()?.then(() => {
            if (!this.hasMobileBrandInputTarget) {
                return
            }

            const parent = this.mobileBrandInputTarget.closest('.input__wrapper')

            if (!parent) {
                return
            }

            (parent.querySelector('.input') as HTMLInputElement)?.focus()
        })
    }

    private handleTopModelInputFocus = async (event: Event) => {
        event.preventDefault()
        event.stopPropagation()

        this.modalOutlet?.open()?.then(() => {
            if (!this.hasMobileModelInputTarget) {
                return
            }

            const parent = this.mobileModelInputTarget.closest('.input__wrapper')

            if (parent) {
                (parent.querySelector('.input') as HTMLInputElement)?.focus()
            }
        })
    }

    private updateMobileModels(brandId: number) {
        const brandData = this.brands[brandId]
        this.resetModelOptions()

        if (!brandData) {
            return
        }

        if (this.hasMobileModelMenuTarget) {
            const menu = this.mobileModelMenuTarget
            const options = Array.from(menu.querySelectorAll('.with-dropdown__menu--item:not(.not-found)') as NodeListOf<HTMLLIElement>)

            options.forEach((option) => {
                const value = option.dataset?.value

                if (value && !brandData.models[value]) {
                    option.style.display = 'none'
                } else {
                    option.style.display = ''
                }
            })
        }

        if (!this.hasMobileModelInputTarget) {
            return
        }

        const modelInput = this.mobileModelInputTarget
        const wrapper = modelInput.closest('.input__wrapper')

        if (!wrapper) {
            return
        }

        const modelSearchInput = wrapper.querySelector('.input') as HTMLInputElement
        const menu = wrapper.querySelector('.with-dropdown__menu')

        if (!menu) {
            return
        }

        const selectedOption = menu.querySelector(`[data-value="${modelInput.value}"]`) as HTMLElement

        if (!selectedOption || selectedOption.style.display === 'none') {
            modelInput.value = ''
            modelSearchInput.value = ''

            modelInput.dispatchEvent(new Event('change', { bubbles: true }))
            modelInput.dispatchEvent(new Event('input', { bubbles: true }))
        }
    }

    private updateMobileBrand(brandId: number, brandName: string) {
        if (!this.hasMobileBrandInputTarget) {
            return
        }

        const brandInput = this.mobileBrandInputTarget
        const wrapper = brandInput.closest('.input__wrapper')

        if (!wrapper) {
            return
        }

        const brandSearchInput = wrapper.querySelector('.input') as HTMLInputElement

        const options = Array.from(wrapper.querySelectorAll('.with-dropdown__menu--item:not(.not-found)') as NodeListOf<HTMLLIElement>)
        const brandOption = options.filter((option) => option.dataset?.value == brandId.toString())

        if (!brandOption.length) {
            brandInput.value = ''
            brandSearchInput.value = ''
        } else {
            brandInput.value = brandId.toString()
            brandSearchInput.value = brandName
        }

        brandInput.dispatchEvent(new Event('change', { bubbles: true }))
        brandInput.dispatchEvent(new Event('input', { bubbles: true }))
    }

    private findBrandByModelId(modelId: number): number | null {
        for (const [brandId, brandData] of Object.entries(this.brands)) {
            if (brandData.models[modelId.toString()]) {
                return parseInt(brandId)
            }
        }

        return null
    }

    private resetModelOptions() {
        if (!this.hasMobileModelMenuTarget) {
            return
        }

        this.mobileModelMenuTarget.querySelectorAll('.with-dropdown__menu--item:not(.not-found)')
            ?.forEach((option) => option.remove())

        for (const [brandId, brandData] of Object.entries(this.brands)) {
            if (!brandData.models) {
                continue
            }

            for (const [modelId, modelName] of Object.entries(brandData.models)) {
                const option = document.createElement('li')

                option.dataset.value = modelId

                option.textContent = modelName
                option.classList.add('with-dropdown__menu--item')
                option.setAttribute('data-select-target', 'item')

                this.mobileModelMenuTarget.appendChild(option)
            }
        }
    }

    private clearModelOptions() {
        if (!this.hasMobileModelMenuTarget) {
            return
        }

        this.mobileModelMenuTarget.querySelectorAll('.with-dropdown__menu--item:not(.not-found)')
            ?.forEach((option) => option.remove())
    }

    private async loadBrands() {
        try {
            const response = await fetch('/api/brands', {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                return
            }

            const data = await response.json()
            this.brands = data.brands
        } catch (error) {
        }
    }
}
